import { useUserInfo } from "@/api/user";
import { isAnswerComplete } from "@/components/ChatHistory/messageStatus";
import { deriveCitationsFromMarkdown } from "@/components/ChatKit/markdownSources";
import FeedbackModal from "@/components/FeedbackModal";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import Sentry from "@/integrations/Sentry";
import { cn } from "@/utils";
import * as analytics from "@/utils/analytics";
import { Message } from "@cohesive/api-client";
import {
  AgentProse,
  Citations,
  Entity,
  Feedback,
  getEntityData,
} from "@cohesive/ui-components";
import { MessageSquare } from "lucide-react";
import React from "react";
import toast from "react-hot-toast";
import { Sources } from "../Sources";
import { MessageFileRecords } from "./components/MessageFileRecords";
import DefaultMessageRenderer from "./renderers/DefaultMessageRenderer";

interface ChatBubbleProps {
  message: Message;
  skeleton?: boolean;
  entities?: Map<string, Entity>;
  children?: React.ReactNode;
}

const Content = ({
  message,
  skeleton,
  entities,
}: {
  message: Message;
  skeleton: boolean;
  entities?: Map<string, Entity>;
}) => {
  if (skeleton) {
    return (
      <div className="my-2">
        <Skeleton className="h-[20px] w-[250px]" />
      </div>
    );
  }

  if (!message.content && !message.result?.data) {
    return null;
  }

  if (message.origin === "user") {
    // Check if this message contains entities
    const hasEntities =
      message.result?.entities && message.result.entities.length > 0;

    if (hasEntities) {
      // Render message with entities flush with other entities (same padding as assistant messages)
      return (
        <div className="w-full mb-2 px-4">
          <DefaultMessageRenderer message={message} entities={entities} />
        </div>
      );
    }

    // Regular user message bubble — square bottom-right corner per the
    // design system's chat kit.
    return (
      <div className="flex flex-col items-end gap-1.5">
        <div
          className={cn(
            "rounded-lg rounded-br-xs bg-n-10 px-4 py-3 font-sans text-md text-n-90 text-pretty [&_p:first-child]:mt-0 [&_p:last-child]:mb-0",
            message.status === "cancelled" && "opacity-50",
          )}
          style={{ wordBreak: "break-word" }}
        >
          <DefaultMessageRenderer message={message} entities={entities} />
        </div>
        {message.status === "cancelled" && (
          <span className="font-sans text-xs text-n-50">Cancelled</span>
        )}
      </div>
    );
  }

  // Agent prose — plain on the surface (no bubble), design typography.
  return (
    <AgentProse>
      <DefaultMessageRenderer message={message} entities={entities} />
    </AgentProse>
  );
};

const ChatBubble = React.forwardRef<HTMLDivElement, ChatBubbleProps>(
  (
    {
      message,
      skeleton = false,
      entities = new Map<string, Entity>(),
      children,
    },
    ref,
  ) => {
    const { data: userInfo } = useUserInfo();
    const [feedbackModalOpen, setFeedbackModalOpen] = React.useState(false);
    const [vote, setVote] = React.useState<"up" | "down" | null>(null);

    // Filter entities to only include those specific to this message
    const messageSpecificEntities = React.useMemo(() => {
      const entityIds = new Set<string>();

      // Handle inline_entity_refs (for later messages with entity references)
      const messageResult = message?.result;
      if (
        messageResult?.inline_entity_refs &&
        Array.isArray(messageResult.inline_entity_refs)
      ) {
        messageResult.inline_entity_refs.forEach((id: string) =>
          entityIds.add(id),
        );
      }

      // Handle full entity objects (for first messages)
      if (message?.result?.entities) {
        message.result.entities.forEach((entity) => {
          const entityData = getEntityData(entity);
          if (entityData?.id) {
            entityIds.add(entityData.id.toString());
          }
        });
      }

      // Build filtered entities map by looking up IDs in the global store
      const filteredEntities = new Map<string, Entity>();
      entityIds.forEach((id) => {
        const entity = entities.get(id);
        if (entity) {
          filteredEntities.set(id, entity);
        }
      });

      return filteredEntities;
    }, [message, entities]);

    // Citation pills under the final answer, derived from the markdown links
    // the answer carries (numbered by first appearance, deduped by domain).
    const citations = React.useMemo(
      () =>
        message.origin === "assistant" && isAnswerComplete(message.status)
          ? deriveCitationsFromMarkdown(message.content)
          : [],
      [message.origin, message.status, message.content],
    );

    const handleVote = (next: "up" | "down" | null) => {
      setVote(next);
      if (next === "up") analytics.trackMessageThumbsUp();
      if (next === "down") analytics.trackMessageThumbsDown();
    };

    const handleFeedbackSubmit = (feedback: string) => {
      const eventId = Sentry.lastEventId();

      const userFeedback = {
        name: userInfo?.user?.name,
        email: userInfo?.user?.email,
        message: feedback,
        associatedEventId: eventId,
      };
      Sentry.captureFeedback(userFeedback);

      analytics.trackMessageFeedbackSubmitted({
        feedback,
        messageId: message.id,
        messageContent: message.content,
      });
      toast.success("Thank you for your feedback");
    };

    if (message.origin === "user") {
      // Check if this message contains entities
      const hasEntities =
        message.result?.entities && message.result.entities.length > 0;

      if (hasEntities) {
        // Render message with entities without bubble background
        return (
          <div
            ref={ref}
            className="w-full flex flex-col break-word group transition-all mb-2 last:mb-8 duration-300"
          >
            <Content
              message={message}
              skeleton={skeleton}
              entities={entities}
            />
          </div>
        );
      }

      // Regular user message with bubble
      return (
        <div
          ref={ref}
          className="max-w-[84%] w-full ml-auto flex flex-col break-word group transition-all mb-2 last:mb-8 duration-300 rounded-lg px-4 py-3 pb-5"
        >
          <div className="flex relative w-full items-end justify-end">
            <div className="flex flex-col w-full relative items-end">
              <div
                className={cn("flex flex-col align-start max-w-full")}
                style={{ wordBreak: "break-word" }}
              >
                <MessageFileRecords
                  fileRecordIds={message.file_record_ids}
                  flowSessionId={message.flow_session_id}
                  align="end"
                />
                <Content
                  message={message}
                  skeleton={skeleton}
                  entities={entities}
                />
              </div>
              {children}
              {isAnswerComplete(message?.status) && (
                <div className="absolute -bottom-[2.5em] -right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 ease-in-out">
                  <Feedback
                    copyText={message.content}
                    onCopied={() => {
                      analytics.trackMessageCopied();
                      toast.success("Message copied to clipboard");
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    // Tool-origin messages (e.g. canvas-node-get responses) currently ship
    // their attachments on `attached_files`, which is a legacy channel and
    // no longer rendered here. Post-migration this branch will receive
    // `file_record_ids` like every other surface; rendering here gives us
    // the wire-ready forward-compat path already.
    if (message.origin === "tool") {
      if (!message.file_record_ids?.length) return null;
      return (
        <div
          ref={ref}
          className="flex flex-col break-word group transition-all duration-300 rounded-lg py-3 px-4"
        >
          <MessageFileRecords
            fileRecordIds={message.file_record_ids}
            flowSessionId={message.flow_session_id}
          />
        </div>
      );
    }

    if (
      message.origin === "assistant" ||
      message.origin === "assistant-tool-call"
    ) {
      return (
        <>
          <div
            ref={ref}
            className="flex flex-col break-word group transition-all mb-8 duration-300 rounded-lg py-3 px-4"
          >
            <div className="flex relative w-full items-start justify-start flex-col">
              <div className="flex flex-col w-full relative">
                <div
                  className={cn("flex flex-col align-start")}
                  style={{ wordBreak: "break-word" }}
                >
                  <Content
                    message={message}
                    skeleton={skeleton}
                    entities={entities}
                  />
                </div>
                {citations.length > 0 && <Citations sources={citations} />}
                {isAnswerComplete(message?.status) && (
                  <div className="absolute -bottom-[2.5em] w-full transition-all duration-200 ease-in-out">
                    <div className="flex flex-row justify-between w-full">
                      <Sources
                        entities={messageSpecificEntities}
                        flowSessionId={message.flow_session_id}
                        flowMessageId={message.id}
                      />
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 ease-in-out">
                        <Feedback
                          copyText={message.content}
                          onCopied={() => {
                            analytics.trackMessageCopied();
                            toast.success("Message copied to clipboard");
                          }}
                          vote={vote}
                          onVote={handleVote}
                        >
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            title="Provide feedback"
                            onClick={() => setFeedbackModalOpen(true)}
                          >
                            <MessageSquare />
                          </Button>
                        </Feedback>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          <FeedbackModal
            open={feedbackModalOpen}
            onOpenChange={setFeedbackModalOpen}
            onSubmit={handleFeedbackSubmit}
          />
        </>
      );
    }

    return null;
  },
);

ChatBubble.displayName = "ChatBubble";

export default React.memo(ChatBubble);
